import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SingleInvoiceUploader from './SingleInvoiceUploader';
import BulkInvoiceUploader from './BulkInvoiceUploader';
import ProcessedInvoicesList from './ProcessedInvoicesList';
import { dgiiApi } from '../../api/dgii';
import { useAuth } from '../../contexts/AuthContext';
import { 
	DocumentArrowUpIcon,
	DocumentTextIcon,
	ClockIcon,
	CheckCircleIcon,
	XMarkIcon,
	InformationCircleIcon,
	ExclamationTriangleIcon,
	BuildingOfficeIcon,
	MagnifyingGlassIcon,
	ChevronDownIcon,
	Cog6ToothIcon,
} from '@heroicons/react/24/outline';

export default function DGIIFormsManager() {
	const { isSuperuser } = useAuth();
	const [activeTab, setActiveTab] = useState('individual'); // 'individual', 'masiva', 'pendientes', 'procesadas'
	const tipoFormulario = '606'; // Solo trabajamos con formulario 606
	const [notification, setNotification] = useState(null);
	const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger para recargar listas
	const [empresas, setEmpresas] = useState([]);
	const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
	const [loadingEmpresas, setLoadingEmpresas] = useState(true);
	const [busquedaEmpresa, setBusquedaEmpresa] = useState('');
	const [mostrarDropdownEmpresa, setMostrarDropdownEmpresa] = useState(false);
	const dropdownEmpresaRef = useRef(null);

	// Filtrar empresas según lo que escribe el usuario (nombre o RNC)
	const empresasFiltradas = empresas.filter((e) => {
		const term = (busquedaEmpresa || '').trim().toLowerCase();
		if (!term) return true;
		const nombre = (e.nombre || '').toLowerCase();
		const rnc = (e.rnc || '').replace(/-/g, '').toLowerCase();
		const termSinGuiones = term.replace(/-/g, '');
		return nombre.includes(term) || rnc.includes(termSinGuiones);
	});
	
	// Cargar empresas al montar el componente
	useEffect(() => {
		cargarEmpresas();
	}, []);

	// Cerrar dropdown al hacer clic fuera
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (dropdownEmpresaRef.current && !dropdownEmpresaRef.current.contains(e.target)) {
				setMostrarDropdownEmpresa(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	
	const cargarEmpresas = async () => {
		try {
			setLoadingEmpresas(true);
			const data = await dgiiApi.getEmpresas();
			const empresasArray = Array.isArray(data) ? data : [];
			
			// Si no hay empresas, intentar crear una desde el perfil del usuario
			if (empresasArray.length === 0) {
				try {
					const empresaCreada = await dgiiApi.crearEmpresaDesdePerfil();
					if (empresaCreada) {
						// Recargar empresas después de crear
						const dataActualizado = await dgiiApi.getEmpresas();
						const empresasActualizadas = Array.isArray(dataActualizado) ? dataActualizado : [];
						setEmpresas(empresasActualizadas);
						
						if (empresasActualizadas.length > 0) {
							const emp = empresasActualizadas[0];
							setEmpresaSeleccionada(emp.id);
							setBusquedaEmpresa(`${emp.nombre} (${emp.rnc})`);
							showNotification('Empresa creada automáticamente desde tu perfil', 'success');
							return;
						}
					}
				} catch (errCrear) {
					// Si falla la creación automática, continuar sin empresas
				}
			}
			
			setEmpresas(empresasArray);

			// Si solo hay una empresa, seleccionarla automáticamente
			if (empresasArray.length === 1) {
				const emp = empresasArray[0];
				setEmpresaSeleccionada(emp.id);
				setBusquedaEmpresa(`${emp.nombre} (${emp.rnc})`);
			} else {
				setEmpresaSeleccionada(null);
			}
		} catch (err) {
			console.error('Error cargando empresas:', err);
			showNotification('Error al cargar empresas', 'error');
		} finally {
			setLoadingEmpresas(false);
		}
	};
	
	// Recargar listas cuando cambiamos a la tab de procesadas
	useEffect(() => {
		if (activeTab === 'procesadas') {
			setRefreshTrigger(prev => prev + 1);
		}
	}, [activeTab]);

	const handleUploadComplete = async () => {
		// Esperar un momento para que el backend procese
		await new Promise(resolve => setTimeout(resolve, 1000));
		
		// Incrementar trigger para recargar listas
		setRefreshTrigger(prev => prev + 1);
		showNotification('Archivo(s) subido(s) exitosamente. Se procesarán automáticamente.', 'success');
	};

	const handleProcessComplete = async () => {
		// Incrementar trigger para recargar listas
		setRefreshTrigger(prev => prev + 1);
		showNotification('Archivos procesados exitosamente. Los formularios se crearon automáticamente según las fechas de las facturas.', 'success');
	};

	const showNotification = (message, type = 'info') => {
		setNotification({ message, type });
		// Auto-ocultar después de 5 segundos
		setTimeout(() => {
			setNotification(null);
		}, 5000);
	};

	const tabs = [
		{ id: 'individual', label: 'Individual', icon: DocumentArrowUpIcon },
		{ id: 'masiva', label: 'Masiva', icon: DocumentTextIcon },
		{ id: 'procesadas', label: 'Procesadas', icon: CheckCircleIcon },
	];

	return (
		<div className="min-h-screen bg-gray-50 py-8">
			{/* Notificación flotante (toast) - no afecta el layout */}
			{notification && (
				<div className="fixed top-20 right-4 z-50 transition-all duration-300">
					<div className={`p-4 rounded-lg shadow-lg flex items-center space-x-3 min-w-[300px] max-w-md ${
						notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
						notification.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
						notification.type === 'warning' ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' :
						'bg-blue-50 border border-blue-200 text-blue-800'
					}`}>
						{notification.type === 'success' && <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />}
						{notification.type === 'error' && <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />}
						{notification.type === 'warning' && <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />}
						{notification.type === 'info' && <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />}
						<p className="flex-1 text-sm">{notification.message}</p>
						<button
							onClick={() => setNotification(null)}
							className="text-gray-400 hover:text-gray-600"
						>
							<XMarkIcon className="h-5 w-5" />
						</button>
					</div>
				</div>
			)}
			
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

				{/* Header */}
				<div className="mb-6 pt-4 sm:pt-6">
					<div className="flex flex-wrap items-center gap-2 mb-2">
						<h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
							Gestor de Formularios DGII 606
						</h1>
						{isSuperuser() && (
							<Link
								to="/admin/settings#llm-procesamiento"
								className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary hover:text-primary/80 hover:bg-primary/5 rounded-md transition-colors"
								title="Configurar flujo de procesamiento (n8n o LLM directo)"
							>
								<Cog6ToothIcon className="h-4 w-4" />
								Configurar flujo
							</Link>
						)}
					</div>
					<p className="text-sm sm:text-base text-gray-600">
						Formulario 606 - Compras de Bienes y Servicios. Los formularios se crean automáticamente según las fechas de las facturas.
					</p>
				</div>
				
				{/* Selector de Empresa - Búsqueda */}
				{!loadingEmpresas && empresas.length > 0 && (
					<div className="mb-6 bg-white rounded-lg shadow-sm p-4 border border-gray-200">
						<div className="flex items-center gap-4">
							<BuildingOfficeIcon className="h-6 w-6 text-primary flex-shrink-0" />
							<div className="flex-1 relative" ref={dropdownEmpresaRef}>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Empresa *
								</label>
								<div className="relative">
									<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
									<input
										type="text"
										value={busquedaEmpresa}
										onChange={(e) => {
											const v = e.target.value;
											setBusquedaEmpresa(v);
											setMostrarDropdownEmpresa(true);
											setEmpresaSeleccionada(null);
										}}
										onFocus={() => setMostrarDropdownEmpresa(true)}
										placeholder="Buscar por nombre o RNC..."
										className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
									/>
									<ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
								</div>
								{mostrarDropdownEmpresa && (
									<ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1">
										{empresasFiltradas.length === 0 ? (
											<li className="px-4 py-3 text-sm text-gray-500">Sin coincidencias</li>
										) : (
											empresasFiltradas.map((empresa) => (
												<li key={empresa.id}>
													<button
														type="button"
														onClick={() => {
															setEmpresaSeleccionada(empresa.id);
															setBusquedaEmpresa(`${empresa.nombre} (${empresa.rnc})`);
															setMostrarDropdownEmpresa(false);
														}}
														className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
															empresaSeleccionada === empresa.id ? 'bg-primary/10 text-primary font-medium' : 'text-gray-700'
														}`}
													>
														{empresa.nombre} ({empresa.rnc}) {empresa.es_default ? '⭐' : ''}
													</button>
												</li>
											))
										)}
									</ul>
								)}
								{!empresaSeleccionada && (
									<p className="mt-1 text-sm text-amber-600">
										⚠️ Debes seleccionar una empresa antes de cargar facturas
									</p>
								)}
							</div>
						</div>
					</div>
				)}
				
				{loadingEmpresas && (
					<div className="mb-6 bg-white rounded-lg shadow-sm p-4 border border-gray-200">
						<div className="flex items-center gap-3">
							<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
							<p className="text-sm text-gray-600">Cargando empresas...</p>
						</div>
					</div>
				)}
				
				{!loadingEmpresas && empresas.length === 0 && (
					<div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
						<div className="flex items-start gap-3">
							<ExclamationTriangleIcon className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
							<div>
								<h3 className="text-sm font-medium text-amber-800 mb-1">
									No tienes empresas registradas
								</h3>
								<p className="text-sm text-amber-700">
									Debes crear al menos una empresa en "Gestión de Empresas" antes de cargar facturas.
								</p>
							</div>
						</div>
					</div>
				)}

				{/* Tabs */}
				<div className="mb-6 border-b border-gray-200">
					<nav className="-mb-px flex space-x-8 overflow-x-auto">
						{tabs.map((tab) => {
							const Icon = tab.icon;
							return (
								<button
									key={tab.id}
									onClick={() => setActiveTab(tab.id)}
									className={`
										whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors
										${activeTab === tab.id
											? 'border-primary text-primary'
											: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}
									`}
								>
									<div className="flex items-center space-x-2">
										<Icon className="h-5 w-5" />
										<span>{tab.label}</span>
									</div>
								</button>
							);
						})}
					</nav>
				</div>

				{/* Contenido de las tabs */}
				<div className="space-y-6">
					{activeTab === 'individual' && (
						<SingleInvoiceUploader
							onUploadComplete={handleUploadComplete}
							empresaSeleccionada={empresaSeleccionada}
							empresasDisponibles={empresas}
							disabled={!empresaSeleccionada}
						/>
					)}

					{activeTab === 'masiva' && (
						<BulkInvoiceUploader
							onUploadComplete={handleUploadComplete}
							empresaSeleccionada={empresaSeleccionada}
							empresasDisponibles={empresas}
							disabled={!empresaSeleccionada}
						/>
					)}

					{activeTab === 'procesadas' && (
						<ProcessedInvoicesList
							key={refreshTrigger} // Forzar re-render cuando cambia el trigger
							onRefresh={refreshTrigger}
							empresaSeleccionada={empresaSeleccionada}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
